import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { CalendarIcon, Sparkles, Check, Camera, Upload } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getChildBirthdayRange } from "@shared/utils";
import Navigation from "@/components/Navigation";

const childProfileSchema = z.object({
  name: z.string().min(1, "Child's name is required"),
  birthday: z.date().nullable().optional(),
  gender: z.enum(["girl", "boy", "other"]).optional().nullable(),
  preferredLanguage: z.string().optional(),
});

type ChildProfileFormData = z.infer<typeof childProfileSchema>;

export default function OnboardingPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [profileCreated, setProfileCreated] = useState(false);
  const totalSteps = 4;

  const form = useForm<ChildProfileFormData>({
    resolver: zodResolver(childProfileSchema),
    defaultValues: {
      name: "",
      birthday: null,
      gender: null,
      preferredLanguage: "en",
    },
  });

  const createProfileMutation = useMutation({
    mutationFn: async (data: ChildProfileFormData) => {
      const payload = {
        name: data.name,
        birthday: data.birthday?.toISOString() || null,
        gender: data.gender || null,
        preferredLanguage: data.preferredLanguage || "en",
      };
      const response = await apiRequest('POST', '/api/child-profiles', payload);
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['/api/child-profiles'] });
    },
  });

  const completeOnboardingMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/onboarding/complete');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      toast({
        title: "Welcome to SnuggleCrafters!",
        description: "You're all set to create magical bedtime stories.",
      });
      navigate("/home");
    },
  });

  const handleNext = async () => {
    if (step === 3) {
      // Validate and create child profile
      const isValid = await form.trigger();
      if (!isValid) return;

      const formData = form.getValues();
      try {
        await createProfileMutation.mutateAsync(formData);
        setProfileCreated(true);
        setStep(step + 1);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to create profile",
          variant: "destructive",
        });
      }
    } else if (step === 4) {
      // Complete onboarding
      completeOnboardingMutation.mutate();
    } else {
      setStep(step + 1);
    }
  };

  const handleSkipProfile = () => {
    // Skip child profile creation and go to success screen
    setStep(4);
  };

  const birthdayRange = getChildBirthdayRange();
  const progress = (step / totalSteps) * 100;

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="SnuggleCrafters Logo" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <CardTitle className="text-center text-2xl">Welcome to SnuggleCrafters!</CardTitle>
          <CardDescription className="text-center">
            Let's get you started with creating magical bedtime stories
          </CardDescription>
          <Progress value={progress} className="mt-4" />
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <Sparkles className="h-16 w-16 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold">Create Personalized Bedtime Stories</h3>
              <p className="text-gray-600">
                SnuggleCrafters uses AI to create unique, engaging bedtime stories tailored to your child's interests, age, and preferences.
              </p>
              <ul className="text-left space-y-3 max-w-md mx-auto">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>Generate unlimited personalized stories</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>Stories in 10 different languages</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>Text-to-speech narration with multiple voices</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <span>Manage multiple child profiles</span>
                </li>
              </ul>
              <Button 
                onClick={handleNext} 
                className="w-full bg-gradient-to-r from-purple-600 to-teal-600"
                data-testid="button-next-welcome"
              >
                Get Started
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="rounded-full bg-gradient-to-br from-rose-100 to-pink-100 p-4">
                  <Camera className="h-16 w-16 text-rose-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold">Upload Photos to Create Cartoon Avatars</h3>
              <p className="text-gray-600">
                Transform your child's photo into a magical cartoon character that appears in every story illustration. Our AI creates a unique avatar in seconds!
              </p>
              <ul className="text-left space-y-3 max-w-md mx-auto">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span>Upload your child's photo during profile creation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span>AI generates a personalized cartoon avatar (30-60 seconds)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span>Avatar appears in all story illustrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span>First profile FREE, additional profiles $1.99 one-time</span>
                </li>
              </ul>
              <Button 
                onClick={handleNext} 
                className="w-full bg-gradient-to-r from-rose-500 to-pink-600"
                data-testid="button-next-photo-explanation"
              >
                Continue
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-xl font-semibold">Create Your First Child Profile</h3>
                <p className="text-sm text-gray-600">This helps us personalize stories for your child</p>
              </div>
              <Form {...form}>
                <form className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Child's Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter child's name" 
                            {...field} 
                            data-testid="input-child-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="birthday"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Birthday (Optional)</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                                data-testid="button-birthday-picker"
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value || undefined}
                              onSelect={field.onChange}
                              disabled={(date) =>
                                date > new Date() || date < birthdayRange.minDate
                              }
                              initialFocus
                              captionLayout="dropdown-buttons"
                              fromYear={birthdayRange.minDate.getFullYear()}
                              toYear={birthdayRange.maxDate.getFullYear()}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          We'll automatically calculate age for story personalization
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender (Optional)</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || undefined}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-gender">
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="girl">Girl</SelectItem>
                            <SelectItem value="boy">Boy</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferredLanguage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Language</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-language">
                              <SelectValue placeholder="Select language" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="es">Spanish</SelectItem>
                            <SelectItem value="fr">French</SelectItem>
                            <SelectItem value="de">German</SelectItem>
                            <SelectItem value="zh">Chinese</SelectItem>
                            <SelectItem value="ja">Japanese</SelectItem>
                            <SelectItem value="pt">Portuguese</SelectItem>
                            <SelectItem value="it">Italian</SelectItem>
                            <SelectItem value="ru">Russian</SelectItem>
                            <SelectItem value="ar">Arabic</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Stories will be generated in this language by default
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </form>
              </Form>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={handleSkipProfile}
                  className="flex-1"
                  data-testid="button-skip-profile"
                >
                  Skip for Now
                </Button>
                <Button 
                  onClick={handleNext}
                  disabled={createProfileMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600"
                  data-testid="button-create-profile"
                >
                  {createProfileMutation.isPending ? "Creating..." : "Create Profile"}
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="rounded-full bg-green-100 p-3">
                    <Check className="h-12 w-12 text-green-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold">
                  {profileCreated ? "You're All Set!" : "Welcome to SnuggleCrafters!"}
                </h3>
                <p className="text-gray-600">
                  {profileCreated 
                    ? "Your child profile has been created. Now let's make it magical!"
                    : "Your account is ready. Let's personalize your experience!"}
                </p>
              </div>

              {/* Post-Creation Callout - Only show if profile was created */}
              {profileCreated && (
                <Card className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50" data-testid="card-photo-upload-callout">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="rounded-full bg-gradient-to-br from-rose-100 to-pink-100 p-3 flex-shrink-0">
                        <Upload className="h-6 w-6 text-rose-600" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <h4 className="font-semibold text-lg">Complete Your Child's Profile</h4>
                        <p className="text-sm text-gray-600">
                          Upload your child's photo to unlock personalized cartoon avatars in story illustrations!
                        </p>
                        <Button 
                          onClick={() => navigate("/child-profiles")}
                          className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700"
                          data-testid="button-add-photo-now"
                        >
                          <Camera className="h-4 w-4 mr-2" />
                          Add Photo Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Callout to create profile if they skipped */}
              {!profileCreated && (
                <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50" data-testid="card-create-profile-callout">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 p-3 flex-shrink-0">
                        <Sparkles className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <h4 className="font-semibold text-lg">Create Your First Child Profile</h4>
                        <p className="text-sm text-gray-600">
                          Add a child profile to unlock personalized stories and cartoon avatars. Your first profile is FREE!
                        </p>
                        <Button 
                          onClick={() => navigate("/child-profiles")}
                          className="w-full bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
                          data-testid="button-create-profile-now"
                        >
                          Create Child Profile
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Next Steps Section */}
              <div className="space-y-4">
                <h4 className="font-semibold text-center">Next Steps: Personalize Your Experience</h4>
                <div className="space-y-3">
                  <div className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border",
                    profileCreated 
                      ? "bg-green-50 border-green-200" 
                      : "bg-gray-50 border-gray-200"
                  )} data-testid="step-create-profile">
                    <div className={cn(
                      "rounded-full p-1 flex-shrink-0",
                      profileCreated 
                        ? "bg-green-100" 
                        : "bg-gray-200"
                    )}>
                      {profileCreated && <Check className="h-4 w-4 text-green-600" />}
                      {!profileCreated && <div className="h-4 w-4" />}
                    </div>
                    <span className={cn(
                      "text-sm",
                      profileCreated ? "text-gray-700" : "text-gray-700 font-medium"
                    )}>
                      Create your first child profile (FREE)
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="step-upload-photo">
                    <div className="rounded-full bg-gray-200 p-1 flex-shrink-0">
                      <div className="h-4 w-4" />
                    </div>
                    <span className="text-sm text-gray-700">Upload a photo to generate cartoon avatar</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="step-create-stories">
                    <div className="rounded-full bg-gray-200 p-1 flex-shrink-0">
                      <div className="h-4 w-4" />
                    </div>
                    <span className="text-sm text-gray-700">Start creating magical stories</span>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>💡 Tip:</strong> You can add more child profiles anytime. Additional profiles are $1.99 each.
                </p>
              </div>

              <Button 
                onClick={handleNext}
                disabled={completeOnboardingMutation.isPending}
                className="w-full bg-gradient-to-r from-purple-600 to-teal-600"
                data-testid="button-start-creating"
              >
                {completeOnboardingMutation.isPending ? "Setting up..." : "Start Creating Stories"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
}
